import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { colors } from '../theme/designTokens';

const SCENARIOS = [
  {
    id: 'kijelzo',
    before: { emoji: '💔', label: 'Törött kijelző', color: '#fee2e2', textColor: '#991b1b', sub: 'Ütés, esés – repedt üveg' },
    after:  { emoji: '✅', label: 'Javított kijelző', color: colors.brandLight, textColor: colors.brandHover, sub: 'Mint az új, 6 hónap garanciával' },
    cta: { label: 'Kijelzőcsere', to: '/szerviz/' },
  },
  {
    id: 'folia',
    before: { emoji: '😬', label: 'Karcos kijelző', color: '#fef9c3', textColor: '#854d0e', sub: 'Kulcsok, homok, táska' },
    after:  { emoji: '✨', label: 'Fóliával védve', color: colors.brandLight, textColor: colors.brandHover, sub: 'Hidrogél fólia – önjavító' },
    cta: { label: 'Fólia árak', to: '/hidrogel/' },
  },
  {
    id: 'akku',
    before: { emoji: '🪫', label: 'Gyorsan merül', color: '#fee2e2', textColor: '#991b1b', sub: '70% akkumulátor – hamar lemerül' },
    after:  { emoji: '🔋', label: 'Új akkumulátor', color: colors.brandLight, textColor: colors.brandHover, sub: '100% kapacitás visszaállítva' },
    cta: { label: 'Akkumulátorcsere', to: '/iphone-akkumulatorcsere-oroshaza/' },
  },
  {
    id: 'telefon',
    before: { emoji: '📵', label: 'Régi, lassú telefon', color: '#f3f4f6', textColor: '#374151', sub: 'Nem bírja az appokat, lassú' },
    after:  { emoji: '📱', label: 'Bevizsgált készülék', color: colors.brandLight, textColor: colors.brandHover, sub: 'Garanciás, kipróbált, ár-arányos' },
    cta: { label: 'Telefonjaink', to: '/telefonok/' },
  },
];

const SingleSlider = ({ scenario }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const getPos = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return 50;
    return Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
  }, []);

  const onMouseDown = (e) => { dragging.current = true; e.preventDefault(); };
  const onMouseMove = (e) => { if (dragging.current) setPos(getPos(e.clientX)); };
  const onMouseUp   = ()  => { dragging.current = false; };

  const onTouchStart = (e) => { dragging.current = true; };
  const onTouchMove  = (e) => {
    if (dragging.current) {
      setPos(getPos(e.touches[0].clientX));
    }
  };
  const onTouchEnd = () => { dragging.current = false; };

  // Billentyűzetes vezérlés a fogantyún (nyilak / Home / End) – a csúszka
  // eddig kizárólag pointerrel volt mozgatható.
  const onHandleKeyDown = (e) => {
    const step = { ArrowLeft: -5, ArrowRight: 5, ArrowDown: -5, ArrowUp: 5 }[e.key];
    if (step === undefined && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    setPos((p) => e.key === 'Home' ? 0 : e.key === 'End' ? 100 :
      Math.min(100, Math.max(0, p + step)));
  };

  const { before, after, cta } = scenario;

  return (
    <div className="flex flex-col">
      {/* Slider */}
      <div
        ref={containerRef}
        className="relative rounded-2xl overflow-hidden select-none touch-none"
        style={{ aspectRatio: '4/3', cursor: 'ew-resize' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* After (base layer) */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center"
          style={{ background: after.color }}
        >
          <span className="text-5xl">{after.emoji}</span>
          <div>
            <p className="font-bold text-base leading-tight" style={{ color: after.textColor }}>{after.label}</p>
            <p className="text-xs mt-1 opacity-80" style={{ color: after.textColor }}>{after.sub}</p>
          </div>
          <span className="absolute top-2 right-3 text-xs font-semibold opacity-60 uppercase tracking-wider" style={{ color: after.textColor }}>Utána</span>
        </div>

        {/* Before (clipped overlay) */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center"
          style={{ background: before.color, clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <span className="text-5xl">{before.emoji}</span>
          <div>
            <p className="font-bold text-base leading-tight" style={{ color: before.textColor }}>{before.label}</p>
            <p className="text-xs mt-1 opacity-80" style={{ color: before.textColor }}>{before.sub}</p>
          </div>
          <span className="absolute top-2 left-3 text-xs font-semibold opacity-60 uppercase tracking-wider" style={{ color: before.textColor }}>Előtte</span>
        </div>

        {/* Divider */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white/80 pointer-events-none"
          style={{ left: `${pos}%` }}
        >
          {/* Handle – valódi slider-vezérlő: fókuszálható, nyilakkal mozgatható.
              A slider-szerep a FOGANTYÚN ül, nem a konténeren, így az Előtte/
              Utána szövegrétegek felolvashatók maradnak. */}
          <div
            role="slider"
            tabIndex={0}
            aria-label={`Előtte–utána összehasonlítás: ${before.label} / ${after.label}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pos)}
            aria-valuetext={`${Math.round(pos)}% – balra az előtte, jobbra az utána nézet`}
            aria-orientation="horizontal"
            onKeyDown={onHandleKeyDown}
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-100 pointer-events-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
              <path d="M5 8L2 8M2 8L4 6M2 8L4 10M11 8L14 8M14 8L12 6M14 8L12 10" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Drag hint (fades after first interaction) */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 pointer-events-none">
          <span className="text-xs bg-black/70 text-white px-2 py-0.5 rounded-full backdrop-blur-sm">← húzd →</span>
        </div>
      </div>

      {/* CTA */}
      <Link
        to={cta.to}
        className="mt-3 inline-flex items-center justify-center gap-2 py-2.5 bg-brand/10 text-brand-hover font-semibold text-sm rounded-xl hover:bg-brand/20 transition-colors"
      >
        {cta.label} <ArrowRight size={15} />
      </Link>
    </div>
  );
};

export default function BeforeAfterSection() {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand-hover font-semibold rounded-full text-sm mb-3">
            Előtte / Utána
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-ink">
            Mihez segítünk hozzá?
          </h2>
          <p className="text-gray-500 mt-2 text-sm">Húzd a csúszkát a változás megtekintéséhez</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {SCENARIOS.map(s => (
            <SingleSlider key={s.id} scenario={s} />
          ))}
        </div>
      </div>
    </section>
  );
}
